/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 19-08-2026
 *
 * Root-service met de feedbackselectie: welke normfout, bouwsuggestie-instantie of simfeedback-loop
 * op dit moment is aangewezen, en de bijbehorende highlight op het modelcanvas (golf 3b; de
 * simfeedback-selectie volgde in golf 3c). Vier consumenten delen hem — de feedbackbalk, de
 * canvas-highlight, de tekstbeschrijving en de simfeedback-stand die de samenwerk-baas samenstelt —
 * waarvan er drie niets met de balk te maken hebben; vandaar een eigen service naast de
 * `FeedbackbalkService`.
 *
 * De selectie is **gedeelde state van de leider** (besluit 15-09-2026, herziening van model B van
 * 18-08-2026). Een volger zet niets zelf: hij vraagt het de leider, die toepast, doorduwt en logt —
 * dezelfde weg die de simfeedback-selectie altijd al ging. Reden: de drie aanwijzingen doen voor de
 * gebruiker hetzelfde (laten zien waar je naar kijkt, ook aan je medegebruikers), en zolang elke
 * client zijn eigen exemplaar zette was er geen eenduidige stand om in een snapshot vast te leggen.
 * De verzendkant en het actielog liggen bij de samenwerk-baas (tot golf 4f bij ng1); die krijgt via
 * `FeedbackSelectieActies` de kant-en-klare logpayload aangeleverd en beslist alleen nog over de
 * route.
 *
 * Er is er altijd maar één tegelijk: elke `selecteer*` wist eerst alles, precies zoals ng1's
 * `resetFeedbackSelectie()` vooraan `highlight_fout`/`markeerBouwsuggestieNode` stond.
 */
import {effect, inject, Injectable, signal, untracked} from '@angular/core';
import {DlModel} from '../app/model/dl-model';
import {GlobalModelcanvasService} from '../app/model/global-modelcanvas.service';
import {NormService} from '../app/engines/norm.service';
import type {SimfeedbackType} from '../app/engines/simfeedback-engine.service';
import {SimfeedbackEngineService} from '../app/engines/simfeedback-engine.service';
import {SamenwerkingService} from './samenwerking.service';
import {highlightNodes, wisFeedbackHighlight} from '../lib/node-highlight';

/**
 * De geselecteerde normfout, op de taalonafhankelijke sleutel van het element plus zijn foutcode.
 */
export interface FoutSelectie {
    elementId: string;
    foutcode: string;
}

/**
 * De geselecteerde bouwsuggestie-instantie: de regel-handle plus de node waarvoor hij geldt. Op de
 * handle en niet op de (vertaalde) titel, zodat hij symmetrisch is met de normfout-sleutel.
 */
export interface BouwsuggestieSelectie {
    handle: string;
    nodeId: string;
}

/**
 * De geselecteerde simfeedback: categorie plus positie binnen die categorie.
 */
export interface SimSelectie {
    type: string;
    index: number;
}

/**
 * De actuele feedbackselectie. Hoogstens één van de drie is gevuld.
 */
export interface FeedbackSelectie {
    fout: FoutSelectie | null;
    bs: BouwsuggestieSelectie | null;
    sim: SimSelectie | null;
}

/**
 * Waar een selectie vandaan komt: een klik in deze client, of een selectie die over de
 * samenwerklijn binnenkwam. Bepaalt of de duw en het actielog nog moeten gebeuren.
 */
export type Herkomst = 'klik' | 'ontvangen';

/**
 * De soort waar een bouwsuggestie-knopje onder staat: de rode of de grijze ballon.
 */
export type BouwsuggestieSoort = 'noodzakelijk' | 'gewenst';

/**
 * De payload van de `foutselect`-actielogregel. Op index en niet op sleutel: de client die logt
 * kent zijn eigen lijst, en het backend resolvet een `foutselect` tegen de laatste `help`-regel van
 * diezelfde client (zie `admin-actionlog-tellingen.md`).
 */
export interface FoutLogPayload {
    foutindex: number;
    elementindex: number;
    fout: string;
    element: string;
}

/**
 * De payload van de `sug_select`-actielogregel: de klik op een genummerd knopje in de
 * bouwsuggestie-ballon van de balk. `soort` staat erin omdat het rood/grijs-onderscheid anders niet
 * in de data terug te vinden is.
 */
export interface BouwsuggestieLogPayload {
    soort: BouwsuggestieSoort;
    regel: string;
    node: string;
}

/**
 * De ketens richting de samenwerk-baas: de verzendkant van het samenwerkbericht en het actielog.
 */
export interface FeedbackSelectieActies {
    /**
     * Een klik op een normfout door een volger: het verzoek gaat naar de leider, die toepast,
     * doorduwt en op onze naam logt. De volger zet zelf niets; de selectie komt terug als
     * `highlight_fout` van de leider. Zelfde opzet als `vraagSimSelectie`.
     */
    vraagFoutselectie: (bericht: FoutSelectie) => void;

    /**
     * Een klik op een bouwsuggestie-instantie door een volger; zelfde route als `vraagFoutselectie`.
     */
    vraagBouwsuggestieselectie: (bericht: {handle: string; nodeId: string; soort: BouwsuggestieSoort}) => void;

    /**
     * Een klik op een normfout bij leider/lokaal, ná het zetten van selectie en highlight: de
     * samenwerk-baas duwt hem naar de volgers en logt `foutselect`.
     */
    meldFoutselectie: (bericht: {elementId: string; foutcode: string; log: FoutLogPayload}) => void;

    /**
     * Een klik op een bouwsuggestie-instantie bij leider/lokaal; zelfde route als `meldFoutselectie`.
     */
    meldBouwsuggestieselectie: (bericht: {handle: string; nodeId: string; soort: BouwsuggestieSoort; log: BouwsuggestieLogPayload}) => void;

    /**
     * Een klik op een simfeedback-loop door een volger: het verzoek gaat als
     * `simfeedbackactie`-simulatiebericht naar de leider, die toepast en terugsynct. Anders dan bij
     * de twee soorten hierboven zet de volger zélf niets — de sim-selectie is onderdeel van de
     * simulatiestatus van de leider en komt via diens sync terug (bestaand protocolgedrag).
     */
    vraagSimSelectie: (desc: SimSelectie) => void;

    /**
     * Een klik op een simfeedback-loop bij leider/lokaal, ná het zetten van selectie en highlight:
     * de samenwerk-baas synct de simfeedback-status naar de volgers en logt `simfb_select`.
     */
    meldSimSelectie: (desc: SimSelectie) => void;

    /**
     * De gebruiker trekt zijn aanwijzing in — een klik op het canvas of op een ander element. Gaat
     * dezelfde weg als het aanwijzen zelf, zodat de highlight bij de andere deelnemers ook loslaat.
     */
    meldSelectieGewist: () => void;
}

@Injectable({providedIn: 'root'})
export class FeedbackSelectieService {

    /**
     * Het foutoverzicht: bron van de indices voor het actielog en van de validatie van de selectie.
     */
    private readonly normService = inject(NormService);

    /**
     * De simfeedback-engine: bron van de loop-ids bij een simfeedback-selectie (golf 3c).
     */
    private readonly simfeedbackEngine = inject(SimfeedbackEngineService);

    /**
     * Het modeldocument, voor de guard op de klikroutes.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De samenwerk-state: een simfeedback-klik van een volger gaat als verzoek naar de leider.
     */
    private readonly samenwerking = inject(SamenwerkingService);

    /**
     * De canvas-eigenaar: bron van de `cy` waarop gehighlight wordt.
     */
    private readonly modelcanvas = inject(GlobalModelcanvasService);

    /**
     * De actuele selectie.
     */
    private readonly _selectie = signal<FeedbackSelectie>({fout: null, bs: null, sim: null});

    /**
     * De actuele selectie, als readonly signal.
     */
    readonly selectie = this._selectie.asReadonly();

    /**
     * Staat het aanwijzen op slot? Naast de readonly-stand telt de replay apart mee: die zet zijn
     * blokkade pas ná het ophalen van de snapshotlijst, en in dat venster mag een klik geen
     * samenwerkbericht en geen logregel meer opleveren. Alleen de klikroute kijkt hiernaar; een
     * ontvangen of teruggehaalde selectie hoort gewoon getekend te worden.
     */
    private get opSlot(): boolean {
        return this.dlModel.fullReadonly || this.dlModel.inReplay();
    }

    /**
     * De door de samenwerk-baas geregistreerde ketens.
     */
    private acties: FeedbackSelectieActies | null = null;

    constructor() {
        /*
         * Een nieuw foutoverzicht kan de geselecteerde fout hebben opgelost. Bestaat hij nog, dan
         * blijft de selectie staan (ook na een taalwissel, want de sleutel is taalonafhankelijk);
         * is hij weg, dan verdwijnt de selectie met de highlight mee. Voorheen wiste
         * `updateFoutweergave` de selectie onvoorwaardelijk bij elke normronde.
         */
        effect(() => {
            this.normService.foutoverzicht(); //we reageren op elke nieuwe opbouw van het overzicht
            const fout = untracked(this._selectie).fout;
            if (fout && !this.normService.zoekFoutregel(fout.elementId, fout.foutcode)) {
                this.wisSelectie();
            }
        });
    }

    /**
     * De samenwerk-baas registreert de verzend- en logketens.
     */
    initialiseer(acties: FeedbackSelectieActies): void {
        this.acties = acties;
    }

    /**
     * Selecteert een normfout en highlight het bijbehorende element. Geeft de logpayload terug, of
     * `null` als die fout hier niet (meer) bestaat — dan gebeurt er niets, want een verkeerd
     * element highlighten is slechter dan stil falen. Een volger zet niets zelf maar vraagt het de
     * leider (zie de kop van dit bestand); bij leider/lokaal gaat de melding meteen door naar de
     * samenwerk-baas (duw en actielog), en bij een ontvangen selectie gebruikt de actietabel de
     * teruggegeven payload zelf.
     */
    selecteerFout(elementId: string, foutcode: string, herkomst: Herkomst = 'klik'): FoutLogPayload | null {
        if (herkomst === 'klik') {
            if (this.opSlot) {
                return null;
            }
            if (this.samenwerking.isVolger()) {
                this.acties?.vraagFoutselectie({elementId, foutcode});
                return null;
            }
        }
        const positie = this.normService.zoekFoutregel(elementId, foutcode);
        if (!positie) {
            return null;
        }
        const regel = this.normService.foutoverzicht()[positie.foutindex];
        this.wisSelectie();
        this._selectie.set({fout: {elementId, foutcode}, bs: null, sim: null});
        this.highlightNodes([elementId]);
        const log: FoutLogPayload = {
            foutindex: positie.foutindex,
            elementindex: positie.elementindex,
            fout: regel.fout,
            element: elementId
        };
        if (herkomst === 'klik') {
            this.acties?.meldFoutselectie({elementId, foutcode, log});
        }
        return log;
    }

    /**
     * Selecteert een bouwsuggestie-instantie en highlight de node. Zelfde routebepaling als
     * `selecteerFout`; `null` als de node niet bestaat.
     */
    selecteerBouwsuggestie(handle: string, nodeId: string, soort: BouwsuggestieSoort, herkomst: Herkomst = 'klik'): BouwsuggestieLogPayload | null {
        if (herkomst === 'klik') {
            if (this.opSlot) {
                return null;
            }
            if (this.samenwerking.isVolger()) {
                this.acties?.vraagBouwsuggestieselectie({handle, nodeId, soort});
                return null;
            }
        }
        if (!nodeId || !this.modelcanvas.getCy()?.$id(nodeId).nonempty()) {
            return null;
        }
        this.wisSelectie();
        this._selectie.set({fout: null, bs: {handle, nodeId}, sim: null});
        this.highlightNodes([nodeId]);
        const log: BouwsuggestieLogPayload = {soort, regel: handle, node: nodeId};
        if (herkomst === 'klik') {
            this.acties?.meldBouwsuggestieselectie({handle, nodeId, soort, log});
        }
        return log;
    }

    /**
     * Selecteert een simfeedback-loop en highlight de elementen ervan. Zelfde routebepaling als de
     * twee soorten hierboven — een volger vraagt het de leider, die toepast, terugsynct en op naam
     * van de volger logt — met één verschil in de uitvoering: de terugweg loopt hier over de
     * simulatielijn, want de sim-selectie reist mee in de simfeedback-stand. De samenwerk-baas
     * gebruikt daarvoor de `'ontvangen'`-variant en doet sync en log er zelf omheen. Een loop die
     * hier niet (meer) bestaat kan bij een ontvangen selectie voorkomen; de selectie wordt dan wél
     * gezet (zoals ng1 deed), alleen de highlight blijft leeg.
     */
    selecteerSimfeedback(type: string, index: number, herkomst: Herkomst = 'klik'): void {
        if (herkomst === 'klik') {
            if (this.opSlot) {
                return;
            }
            if (this.samenwerking.isVolger()) {
                this.acties?.vraagSimSelectie({type, index});
                return;
            }
        }
        this.wisSelectie();
        this._selectie.set({fout: null, bs: null, sim: {type, index}});
        this.highlightNodes(this.simfeedbackEngine.feedback()[type as SimfeedbackType]?.[index]);
        if (herkomst === 'klik') {
            this.acties?.meldSimSelectie({type, index});
        }
    }

    /**
     * De gebruiker klikt zijn aanwijzing weg: op het canvas, of door een ander element te kiezen.
     * Het intrekken gaat dezelfde weg als het aanwijzen zelf — het reist mee, zodat de andere
     * deelnemers hem ook loslaten (bevinding testronde a4, 09-09-2026; het bleef bij de ander
     * hangen), en een volger vraagt het de leider in plaats van zelf te wissen (besluit 15-09-2026).
     * Stond er niets aan, dan gaat er niets de deur uit: een klik op een leeg canvas is geen
     * gebeurtenis voor de anderen.
     *
     * Geen actielogregel, net als in AngularJS: alleen het aanwijzen wordt vastgelegd.
     */
    wisSelectieDoorKlik(): void {
        const stand = this._selectie();
        if (this.opSlot || (!stand.fout && !stand.bs && !stand.sim)) {
            return;
        }
        if (!this.samenwerking.isVolger()) {
            this.wisSelectie();
        }
        this.acties?.meldSelectieGewist();
    }

    /**
     * Wist alle selecties en de highlights op het canvas.
     */
    wisSelectie(): void {
        const cy = this.modelcanvas.getCy();
        if (cy) {
            wisFeedbackHighlight(cy);
        }
        this._selectie.set({fout: null, bs: null, sim: null});
    }

    /**
     * Highlight de opgegeven nodes zonder de selectie te raken: voor de hover op de
     * tekstbeschrijving en voor de loop van een simfeedback-selectie. Die laatste komt bij een
     * volger uit de feedbackset van de leider, dus een lege of ontbrekende lijst kan voorkomen.
     */
    highlightNodes(ids: string[] | null | undefined): void {
        const cy = this.modelcanvas.getCy();
        if (cy && ids?.length) {
            highlightNodes(cy, ids);
        }
    }
}
